/** Build a minimal SVG that draws a raster as a single rect filled with a pattern. */

const RASTER_PATTERN_ID = "ldRasterFill";
/** Longest edge (px) before we downscale for preview / warp performance. */
export const LATTICE_RASTER_MAX_EDGE = 4096;

function escapeAttrValue(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

export function rasterDataUrlToLatticeSvg(
    dataUrl: string,
    width: number,
    height: number,
): string {
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    const esc = escapeAttrValue(dataUrl);
    return [
        `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${w} ${h}">`,
        `<defs><pattern id="${RASTER_PATTERN_ID}" patternUnits="userSpaceOnUse" x="0" y="0" width="${w}" height="${h}">`,
        `<image href="${esc}" xlink:href="${esc}" preserveAspectRatio="none" x="0" y="0" width="${w}" height="${h}" />`,
        `</pattern></defs>`,
        `<rect x="0" y="0" width="${w}" height="${h}" fill="url(#${RASTER_PATTERN_ID})" />`,
        `</svg>`,
    ].join("");
}

export function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === "string") resolve(reader.result);
            else reject(new Error("Unable to read file."));
        };
        reader.onerror = () => reject(new Error("Error reading file."));
        reader.readAsDataURL(file);
    });
}

export function loadImageNaturalSize(dataUrl: string): Promise<{ w: number; h: number }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const w = img.naturalWidth;
            const h = img.naturalHeight;
            if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
                reject(new Error("Image has no dimensions."));
                return;
            }
            resolve({ w, h });
        };
        img.onerror = () => reject(new Error("Could not decode image."));
        img.src = dataUrl;
    });
}

/**
 * If width or height exceeds `maxEdge`, draw to a canvas and return a PNG
 * data URL plus new dimensions (preserves aspect ratio).
 */
export async function downscaleRasterDataUrlIfNeeded(
    dataUrl: string,
    w: number,
    h: number,
    maxEdge: number,
): Promise<{ dataUrl: string; w: number; h: number }> {
    if (w <= maxEdge && h <= maxEdge) return { dataUrl, w, h };
    const scale = maxEdge / Math.max(w, h);
    const nw = Math.max(1, Math.round(w * scale));
    const nh = Math.max(1, Math.round(h * scale));
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Could not decode image for resize."));
        img.src = dataUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = nw;
    canvas.height = nh;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { dataUrl, w, h };
    ctx.drawImage(img, 0, 0, nw, nh);
    return { dataUrl: canvas.toDataURL("image/png"), w: nw, h: nh };
}

const RASTER_EXT = /\.(png|jpe?g|webp|gif|bmp|avif)$/i;

export function isRasterImageFile(file: File): boolean {
    const t = file.type.toLowerCase();
    if (t === "image/svg+xml") return false;
    if (t.startsWith("image/")) return true;
    return RASTER_EXT.test(file.name);
}

export function isSvgFile(file: File): boolean {
    const t = file.type.toLowerCase();
    if (t.includes("svg")) return true;
    return file.name.toLowerCase().endsWith(".svg");
}
